import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Loader2, Save, Mail, Send, Paperclip, Users } from "lucide-react";

const ORDER = ["preoutlook", "proposta", "reminder_cliente", "ordine_cliente", "ordine_admin", "reminder_admin", "pagamento_cliente", "pagamento_consulente", "pagamento_admin", "appuntamento_cliente", "appuntamento_admin", "lead_qualificato", "lead_assegnato", "candidato_partner", "lead_rapido", "benvenuto_partner", "lead_self_service", "backup_notturno", "alert_email_critico"];

export default function EmailTemplates() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tpls, setTpls] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [flags, setFlags] = useState({});
  const [flagLabels, setFlagLabels] = useState({});
  const [savingFlags, setSavingFlags] = useState(false);
  const [recipients, setRecipients] = useState({});
  const [staffUsers, setStaffUsers] = useState([]);
  const [savingRec, setSavingRec] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/settings/email-templates"),
      api.get("/settings/email-attachments"),
      api.get("/settings/email-recipients"),
    ])
      .then(([rt, rf, rr]) => {
        setTpls(rt.data.templates || {});
        setFlags(rf.data.flags || {});
        setFlagLabels(rf.data.labels || {});
        setRecipients(rr.data.recipients || {});
        setStaffUsers(rr.data.users || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleRecipient = async (key, userId) => {
    const cur = recipients[key]?.user_ids || [];
    const nextIds = cur.includes(userId) ? cur.filter((i) => i !== userId) : [...cur, userId];
    const nextRec = { ...recipients, [key]: { configured: true, user_ids: nextIds } };
    setRecipients(nextRec);
    const payload = {};
    for (const k of Object.keys(nextRec)) payload[k] = nextRec[k].user_ids || [];
    setSavingRec(true);
    try {
      const { data } = await api.put("/settings/email-recipients", { templates: payload });
      setRecipients(data.recipients || nextRec);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    } finally {
      setSavingRec(false);
    }
  };

  const toggleFlag = async (key, value) => {
    const prev = flags;
    const next = { ...flags, [key]: value };
    setFlags(next);
    setSavingFlags(true);
    try {
      const { data } = await api.put("/settings/email-attachments", { flags: next });
      setFlags(data.flags || next);
      toast.success("Impostazione allegato aggiornata");
    } catch (e) {
      setFlags(prev);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingFlags(false);
    }
  };

  const setField = (key, field, value) =>
    setTpls((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));

  const toggleActive = async (key, value) => {
    const prev = tpls;
    const next = { ...tpls, [key]: { ...tpls[key], active: value } };
    setTpls(next);
    const payload = {};
    for (const k of Object.keys(next)) {
      const t = next[k];
      payload[k] = { subject: t.subject || "", title: t.title || "", body: t.body || "", active: t.active !== false };
    }
    try {
      const { data } = await api.put("/settings/email-templates", { templates: payload });
      setTpls(data.templates || next);
      toast.success(value ? "Email attivata" : "Email disattivata");
    } catch (e) {
      setTpls(prev);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const insertPlaceholder = (key, ph) =>
    setTpls((t) => ({ ...t, [key]: { ...t[key], body: `${t[key].body || ""}{${ph}}` } }));

  const save = async () => {
    const payload = {};
    for (const k of Object.keys(tpls)) {
      const t = tpls[k];
      if (!t.subject?.trim() || !t.title?.trim() || !t.body?.trim()) {
        toast.error("Oggetto, titolo e corpo sono obbligatori per ogni email");
        return;
      }
      payload[k] = { subject: t.subject.trim(), title: t.title.trim(), body: t.body.trim(), active: t.active !== false };
    }
    setSaving(true);
    try {
      const { data } = await api.put("/settings/email-templates", { templates: payload });
      setTpls(data.templates || {});
      toast.success("Testi email salvati");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const keys = ORDER.filter((k) => tpls[k]).concat(Object.keys(tpls).filter((k) => !ORDER.includes(k)));

  const sendTest = async (key) => {
    const t = tpls[key];
    setTesting(key);
    try {
      const { data } = await api.post("/settings/email-templates/test", {
        key,
        subject: t.subject,
        title: t.title,
        body: t.body,
      });
      toast.success(`Email di prova inviata a ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito");
    } finally {
      setTesting(null);
    }
  };

  return (
    <div>
      <header className="sticky top-14 lg:top-0 z-10 h-16 border-b border-border bg-background flex items-center justify-between px-4 lg:px-8">
        <div>
          <h1 className="text-xl font-heading font-bold">Email automatiche</h1>
          <p className="text-xs text-muted-foreground">Testi delle email inviate automaticamente dal sistema, personalizzabili</p>
        </div>
        {isAdmin && (
          <Button onClick={save} disabled={saving || loading} data-testid="save-email-templates-btn" className="font-semibold">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Salva testi
          </Button>
        )}
      </header>

      <div className="p-4 lg:p-8">
        {loading ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="max-w-3xl space-y-5">
            {!isAdmin && (
              <div className="text-xs text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded p-3">
                Solo gli amministratori possono modificare i testi delle email.
              </div>
            )}

            <Card className="rounded-lg border-border p-5" data-testid="email-attachments-card">
              <div className="flex items-start gap-3 mb-4">
                <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Paperclip className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">Allegato PDF alle email</div>
                  <div className="text-xs text-muted-foreground">Scegli quali email inviano in allegato il PDF (proposta o contratto firmato).</div>
                </div>
              </div>
              <div className="space-y-2.5">
                {Object.keys(flagLabels).map((k) => (
                  <div key={k} className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                    <span className="text-sm text-foreground">{flagLabels[k]}</span>
                    <Switch checked={!!flags[k]} disabled={!isAdmin || savingFlags} onCheckedChange={(v) => toggleFlag(k, v)} data-testid={`email-attach-${k}`} />
                  </div>
                ))}
              </div>
            </Card>

            {keys.map((key) => {
              const t = tpls[key];
              return (
                <Card key={key} className="rounded-lg border-border p-5" data-testid={`email-tpl-${key}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <Mail className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        {t.label || key}
                        {t.active === false && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/15 text-red-600" data-testid={`email-${key}-inactive-badge`}>Disattivata</span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">{t.descrizione}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex flex-col items-end">
                        <Switch
                          checked={t.always_active ? true : t.active !== false}
                          disabled={!isAdmin || t.always_active}
                          onCheckedChange={(v) => toggleActive(key, v)}
                          data-testid={`email-${key}-active`}
                          title={t.always_active ? "Email critica: sempre attiva" : "Attiva/disattiva l'invio di questa email"}
                        />
                        <span className="text-[9px] text-muted-foreground mt-1">{t.always_active ? "Sempre attiva" : (t.active !== false ? "Attiva" : "Inattiva")}</span>
                      </div>
                      {isAdmin && (
                        <Button variant="outline" size="sm" onClick={() => sendTest(key)} disabled={testing === key} data-testid={`email-${key}-test`} title="Invia una prova a me stesso">
                          {testing === key ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />} Invia prova
                        </Button>
                      )}
                    </div>
                  </div>

                  {recipients[key] && (
                    <div className="mb-4 rounded-md border border-border bg-secondary/20 p-3" data-testid={`email-${key}-recipients`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                        <Users className="h-3 w-3" /> Destinatari interni
                        {(key === "lead_assegnato" || key === "lead_qualificato") && <span className="normal-case tracking-normal text-[10px]">· oltre all'assegnatario del lead</span>}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {staffUsers.map((u) => {
                          const on = (recipients[key].user_ids || []).includes(u.id);
                          return (
                            <button key={u.id} type="button" disabled={!isAdmin || savingRec}
                              onClick={() => toggleRecipient(key, u.id)}
                              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${on ? "bg-primary text-primary-foreground border-primary font-semibold" : "bg-background text-muted-foreground border-border hover:border-primary/50"}`}
                              data-testid={`email-${key}-rcpt-${u.id}`} title={u.email}>
                              {u.name}
                            </button>
                          );
                        })}
                      </div>
                      {(recipients[key].user_ids || []).length === 0 && key !== "lead_assegnato" && key !== "lead_qualificato" && (
                        <div className="text-[10px] text-red-600 mt-2">Nessun destinatario selezionato: questa email non verrà inviata.</div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Oggetto</Label>
                      <Input value={t.subject || ""} disabled={!isAdmin} onChange={(e) => setField(key, "subject", e.target.value)} data-testid={`email-${key}-subject`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Titolo (intestazione nel corpo)</Label>
                      <Input value={t.title || ""} disabled={!isAdmin} onChange={(e) => setField(key, "title", e.target.value)} data-testid={`email-${key}-title`} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Corpo del messaggio (HTML consentito)</Label>
                      <Textarea value={t.body || ""} disabled={!isAdmin} onChange={(e) => setField(key, "body", e.target.value)} rows={7} className="font-mono text-xs" data-testid={`email-${key}-body`} />
                    </div>
                    {(t.placeholders || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Segnaposto:</span>
                        {t.placeholders.map((ph) => (
                          <button
                            key={ph}
                            type="button"
                            disabled={!isAdmin}
                            onClick={() => insertPlaceholder(key, ph)}
                            className="text-[11px] font-mono px-2 py-0.5 rounded bg-secondary text-foreground hover:bg-primary/15 disabled:opacity-50 transition-colors"
                            data-testid={`email-${key}-ph-${ph}`}
                            title="Clicca per inserire nel corpo"
                          >
                            {`{${ph}}`}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
